import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, createElementVNode as _createElementVNode } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "w-full relative z-[9999]"
}
const _hoisted_2 = {
  key: 2,
  class: "w-full h-full md:h-auto lg:flex relative"
}
const _hoisted_3 = { class: "w-full lg:min-w-0 bg-swish-sm md:bg-swish-md lg:bg-swish-lg bg-right-top bg-no-repeat md:bg-fixed flex flex-col overflow-x-hidden" }
const _hoisted_4 = { class: "grow" }
const _hoisted_5 = { key: 0 }
const _hoisted_6 = {
  key: 0,
  class: "pt-15 max-w-[60rem]",
  "data-test-id": "disclaimer_component"
}

import { getCurrentInstance, onMounted, onUnmounted, computed, watch, ref } from 'vue';
import SelfServiceBreadcrumbs from './SelfServiceBreadcrumbs.vue';
import { ComponentRendering, Field, Placeholder, RichText } from '@sitecore-jss/sitecore-jss-vue';
import GlobalAlert from '@/components/GlobalAlert/GlobalAlert.vue';
import { GlobalAlertsVisibility } from '@/enums/global-alerts.enum';
import { INameValue } from '@/interfaces/entities/name-value.interface';
import { toBool } from '@/utils/convert';
import { useAccountStore, usePageStore, useLiveChatStore } from '@/store';
import FullScreenSpinner from '../FullScreenSpinner/FullScreenSpinner.vue';


export default /*@__PURE__*/_defineComponent({
  __name: 'SelfServiceLayout',
  props: {
    rendering: {},
    fields: {}
  },
  setup(__props: any) {

const props = __props;

const outerClass = props.rendering?.params?.Styles;
const inlineStyle = props.rendering?.params?.inlineStyle;
const context: Record<string, Field> = {};
props.fields.fields.forEach((item) => {
  context[item.name] = { value: item.value };
});

const showBreadcrumbs = toBool(context.showBreadcrumbs?.value);

if (showBreadcrumbs) {
  const pageStore = usePageStore();
  pageStore.breadcrumbs = props.fields.breadcrumbs;
}

const app = getCurrentInstance();
const contentEditing = app?.appContext.config.globalProperties.$jss.sitecoreContext()?.pageEditing === true;
const routeData = app?.appContext.config.globalProperties.$jss.routeData();

const displayGlobalAlerts = routeData?.fields?.GlobalAlerts?.fields?.globalAlerts?.filter(
  (x: any) =>
    x.fields?.visibility?.value === GlobalAlertsVisibility.All ||
    x.fields?.visibility?.value === GlobalAlertsVisibility.SelfService
);

const showMenu = computed(() => contentEditing || toBool(context.showNavigation?.value));

const liveChatStore = useLiveChatStore();

onMounted(() => {
  // Need to wait for styles to load. nextTick doesn't cut it unfortunately.
  setTimeout(() => {
    updateNavPosition(null);
  }, 10);

  liveChatStore.setChatEnabled(true);

  window.addEventListener('scroll', updateNavPosition);
  window.addEventListener('resize', updateNavPosition);
});

onUnmounted(() => {
  window.removeEventListener('scroll', updateNavPosition);
  window.removeEventListener('resize', updateNavPosition);
});

const getAlertsHeight = (removedAlert?: HTMLDivElement) => {
  const alerts = document.querySelectorAll('[data-global-alert]');
  let alertsHeight = 0;
  for (let alert of alerts) {
    if (alert != removedAlert) {
      alertsHeight += alert.clientHeight;
    }
  }
  return alertsHeight;
};

const updateNavPosition = (e: any | null, removedAlert?: HTMLDivElement) => {
  const drawer = document.getElementById('self-service-menu-drawer') as HTMLDivElement;
  if (!drawer) return;
  if (window.innerWidth < 992) {
    const stickyNavTop = document.getElementById('sticky-header')?.getBoundingClientRect().top ?? 0;
    const alertsHeight = getAlertsHeight(removedAlert);
    const scrollTop = document.body.style.top ? parseInt(document.body.style.top || '0') * -1 : window.scrollY;
    drawer.style.top = `${stickyNavTop == 0 ? scrollTop : stickyNavTop + scrollTop}px`;
    drawer.style.maxHeight = window.scrollY < alertsHeight ? `calc(100vh - ${alertsHeight - window.scrollY}px)` : '';
  } else {
    drawer.style.top = '';
    drawer.style.maxHeight = '';
  }
};

const accountStore = useAccountStore();
const authRequired = props.fields?.fields?.find((field) => field.name == 'AuthenticationRequired')?.value == '1';
// do not show spinner if auth not required / experience editor / d365Id loaded
const loaded = ref(!authRequired || contentEditing || !!accountStore.identityDetails?.d365Id);
!contentEditing &&
  watch(
    () => accountStore.identityDetails?.d365Id,
    (d365Id) => (loaded.value = !!d365Id)
  );

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    ((_unref(displayGlobalAlerts)?.length ?? 0) > 0)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(displayGlobalAlerts), (item, itemIndex) => {
            return (_openBlock(), _createBlock(GlobalAlert, {
              key: itemIndex,
              type: item?.fields?.type?.value,
              content: item?.fields?.content?.value,
              visibility: item?.fields?.visibility?.value,
              onCloseAlert: _cache[0] || (_cache[0] = (el) => updateNavPosition(null, el))
            }, null, 8, ["type", "content", "visibility"]))
          }), 128))
        ]))
      : _createCommentVNode("", true),
    (!loaded.value)
      ? (_openBlock(), _createBlock(FullScreenSpinner, { key: 1 }))
      : (_openBlock(), _createElementBlock("div", _hoisted_2, [
          (showMenu.value)
            ? (_openBlock(), _createBlock(_unref(Placeholder), {
                key: 0,
                name: "jss-page-menu",
                rendering: _ctx.rendering
              }, null, 8, ["rendering"]))
            : _createCommentVNode("", true),
          _createElementVNode("div", _hoisted_3, [
            _createElementVNode("main", {
              class: _normalizeClass(["grow flex flex-col pt-8 pb-12 md:py-18 px-4 md:px-8 xl:px-25 w-full lg:max-w-[1144px]", { 'mx-auto': !showMenu.value }])
            }, [
              _createElementVNode("div", _hoisted_4, [
                (showMenu.value)
                  ? (_openBlock(), _createElementBlock("div", {
                      key: 0,
                      class: _normalizeClass({ 'pb-12 mb-2 border-b border-racq-white': _unref(showBreadcrumbs) })
                    }, [
                      (_unref(showBreadcrumbs))
                        ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                            _createVNode(SelfServiceBreadcrumbs)
                          ]))
                        : _createCommentVNode("", true),
                      _createVNode(_unref(Placeholder), {
                        name: "jss-ss-header",
                        rendering: _ctx.rendering
                      }, null, 8, ["rendering"])
                    ], 2))
                  : _createCommentVNode("", true),
                _createElementVNode("div", {
                  class: _normalizeClass(["flex flex-col pt-12 w-full", _unref(outerClass)]),
                  style: _normalizeStyle(_unref(inlineStyle))
                }, [
                  _createVNode(_unref(Placeholder), {
                    name: "jss-ss-main",
                    rendering: _ctx.rendering
                  }, null, 8, ["rendering"])
                ], 6)
              ]),
              (context?.disclaimer?.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                    _createVNode(_unref(RichText), {
                      field: context?.disclaimer as Field<string>,
                      class: "self-end"
                    }, null, 8, ["field"])
                  ]))
                : _createCommentVNode("", true)
            ], 2),
            _createVNode(_unref(Placeholder), {
              name: "jss-page-footer",
              rendering: _ctx.rendering
            }, null, 8, ["rendering"])
          ])
        ]))
  ], 64))
}
}

})